import type { OrderBookDto, OrderBookLevelDto } from "../shared/contracts.ts";
import { numberValue } from "./format.ts";

const CHART_WIDTH = 1_000;
const CHART_HEIGHT = 130;
const CHART_CENTER = CHART_WIDTH / 2;

export interface DepthGraphModel {
  askAreaPath: string;
  askLinePath: string;
  askTotal: number;
  bidAreaPath: string;
  bidLinePath: string;
  bidTotal: number;
  referencePrice?: number;
  spread?: number;
  spreadPercent?: number;
}

interface Point {
  x: number;
  y: number;
}

function quantity(level: OrderBookLevelDto): number {
  return Math.max(0, numberValue(level.quantity) ?? 0);
}

function total(levels: OrderBookLevelDto[]): number {
  return levels.reduce((sum, level) => sum + quantity(level), 0);
}

function coordinate(value: number): string {
  return String(Math.round(value * 100) / 100);
}

function linePath(points: Point[]): string {
  if (points.length === 0) return "";
  const [first, ...rest] = points;
  return rest.reduce(
    (path, point) => `${path} H ${coordinate(point.x)} V ${coordinate(point.y)}`,
    `M ${coordinate(first!.x)} ${coordinate(first!.y)}`,
  );
}

function areaPath(points: Point[]): string {
  if (points.length === 0) return "";
  const first = points[0]!;
  const last = points.at(-1)!;
  return `${linePath(points)} L ${coordinate(last.x)} ${CHART_HEIGHT} L ${coordinate(first.x)} ${CHART_HEIGHT} Z`;
}

function sidePoints(
  levels: OrderBookLevelDto[],
  side: "ask" | "bid",
  maximumTotal: number,
): Point[] {
  if (levels.length === 0) return [];
  let cumulative = 0;
  const points = levels.map((level, index) => {
    cumulative += quantity(level);
    const distance = ((index + 1) / levels.length) * CHART_CENTER;
    return {
      x: side === "bid" ? CHART_CENTER - distance : CHART_CENTER + distance,
      y: CHART_HEIGHT - (cumulative / maximumTotal) * CHART_HEIGHT,
    };
  });
  if (side === "bid") return [...points.reverse(), { x: CHART_CENTER, y: CHART_HEIGHT }];
  return [{ x: CHART_CENTER, y: CHART_HEIGHT }, ...points];
}

export function buildDepthGraph(orderBook: OrderBookDto): DepthGraphModel {
  const bids = orderBook.bids.slice(0, 20);
  const asks = orderBook.asks.slice(0, 20);
  const bidTotal = total(bids);
  const askTotal = total(asks);
  const maximumTotal = Math.max(1, bidTotal, askTotal);
  const bidPoints = sidePoints(bids, "bid", maximumTotal);
  const askPoints = sidePoints(asks, "ask", maximumTotal);
  const bestBid = numberValue(bids[0]?.price);
  const bestAsk = numberValue(asks[0]?.price);
  const spread = bestBid !== undefined && bestAsk !== undefined
    ? Math.max(0, bestAsk - bestBid)
    : undefined;

  return {
    askAreaPath: areaPath(askPoints),
    askLinePath: linePath(askPoints),
    askTotal,
    bidAreaPath: areaPath(bidPoints),
    bidLinePath: linePath(bidPoints),
    bidTotal,
    referencePrice: numberValue(orderBook.lastPrice) ?? bestAsk ?? bestBid,
    spread,
    spreadPercent: spread !== undefined && bestBid
      ? spread / Math.abs(bestBid) * 100
      : undefined,
  };
}

export function formatCompactQuantity(value: number): string {
  const absolute = Math.abs(value);
  const divisor = absolute >= 1_000_000 ? 1_000_000 : absolute >= 1_000 ? 1_000 : 1;
  const suffix = divisor === 1_000_000 ? "m" : divisor === 1_000 ? "k" : "";
  return `${new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: divisor === 1 ? 0 : 2,
  }).format(value / divisor)}${suffix}`;
}
